import React from "react";
import BlogPostLayout, {
  A,
  Callout,
  CodeBlock,
  H2,
  InlineCode,
  P,
  UL,
} from "@/components/BlogPostLayout";
import { BLOG_POSTS } from "@/lib/blogPosts";

const post = BLOG_POSTS.find((p) => p.slug === "stateless-mcp-server-nextjs")!;

export default function StatelessMcpPost() {
  return (
    <BlogPostLayout post={post}>
      <P>
        FrazzleCRM has a built-in{" "}
        <A href="https://modelcontextprotocol.io">
          MCP (Model Context Protocol)
        </A>{" "}
        server: point Claude, ChatGPT, or any MCP client at your workspace and
        it can read, draft, and audit your automations. It is listed in the
        official MCP Registry, and it is not a separate service — the whole
        thing is <strong>one API route</strong> inside the same Next.js
        pages-router app that serves our marketing site and product. This post
        is the honest write-up of how that works and which decisions turned
        out to matter.
      </P>

      <H2>Why Streamable HTTP, and why stateless</H2>
      <P>
        MCP has two remote transports in the wild: the older SSE transport
        (server-push over a long-lived stream) and{" "}
        <strong>Streamable HTTP</strong>, where the client POSTs JSON-RPC
        messages and the server may answer with plain JSON <em>or</em> upgrade
        to a stream. The under-appreciated detail in the spec: the stream is
        optional. If your server never needs to push server-initiated
        messages, you can answer every POST with a plain{" "}
        <InlineCode>application/json</InlineCode> body and never hold a
        connection open.
      </P>
      <P>
        That subset — call it <em>stateless Streamable HTTP</em> — fits a
        Next.js API route perfectly. No session table, no sticky routing, no
        SSE plumbing through proxies, and a single-replica deploy can restart
        any time without breaking clients. Our entire transport layer is a
        standard <InlineCode>NextApiRequest</InlineCode> handler:
      </P>
      <CodeBlock>{`if (req.method === "GET" || req.method === "DELETE") {
  // Stateless server: no server-initiated streams, no sessions to delete.
  return res.status(405).json({
    error: "stateless MCP server — POST JSON-RPC messages",
  });
}`}</CodeBlock>
      <P>
        Clients that probe for the optional GET stream get a clear 405 and
        carry on with plain POSTs. Every MCP client we have tested — claude.ai
        custom connectors, Claude Code, Cursor — is happy with that.
      </P>

      <H2>The whole protocol is a switch statement</H2>
      <P>
        Strip away the transport and an MCP server is small: four methods
        cover a tools-only server. Ours handles{" "}
        <InlineCode>initialize</InlineCode>, <InlineCode>ping</InlineCode>,{" "}
        <InlineCode>tools/list</InlineCode>, and{" "}
        <InlineCode>tools/call</InlineCode>; notifications (messages without
        an <InlineCode>id</InlineCode>) get no response, and a
        notification-only POST returns <InlineCode>202 Accepted</InlineCode>{" "}
        with an empty body, per spec. Batches work by mapping the same handler
        over an array.
      </P>
      <P>
        Protocol versioning is worth getting right because clients pin
        different dates. We accept the versions we actually know and echo the
        requested one back when we support it:
      </P>
      <CodeBlock>{`const PROTOCOL_VERSIONS = new Set([
  "2024-11-05", "2025-03-26", "2025-06-18",
]);

case "initialize":
  return rpcResult(id, {
    protocolVersion: PROTOCOL_VERSIONS.has(requested)
      ? requested
      : DEFAULT_PROTOCOL,
    capabilities: { tools: { listChanged: false } },
    serverInfo: SERVER_INFO,
    instructions: SERVER_INSTRUCTIONS,
  });`}</CodeBlock>
      <P>
        <InlineCode>listChanged: false</InlineCode> is the stateless story
        again: we could not notify anyone even if the tool list changed,
        because there is no channel to notify on. Say so up front.
      </P>

      <H2>Multi-tenancy: scope at the door, not in the tools</H2>
      <P>
        Every request must carry an API key (
        <InlineCode>Authorization: Bearer frz_…</InlineCode> or{" "}
        <InlineCode>x-api-key</InlineCode>). The key resolves to exactly one
        company, and that <InlineCode>companyId</InlineCode> is the only
        context a tool ever receives. Tools cannot ask for a different tenant
        because the parameter does not exist — the scoping decision is made
        once, at authentication, instead of being re-made (and eventually
        forgotten) inside thirteen tool implementations.
      </P>
      <P>
        One CORS note that cost us an hour: browser-based clients like
        claude.ai send preflights with MCP-specific headers, so your{" "}
        <InlineCode>Access-Control-Allow-Headers</InlineCode> needs{" "}
        <InlineCode>Mcp-Session-Id</InlineCode> and{" "}
        <InlineCode>MCP-Protocol-Version</InlineCode> alongside the usual
        suspects, even on a server that never uses sessions.
      </P>

      <H2>Tool errors are results, not errors</H2>
      <P>
        JSON-RPC has a protocol-error shape, and it is the wrong place for
        domain errors. If a model asks for an automation that does not exist
        and gets a protocol error back, many clients treat the call as broken
        rather than wrong, and the model retries blindly. Return domain
        failures as a tool <em>result</em> with{" "}
        <InlineCode>isError: true</InlineCode> and a human-readable message,
        and the model reads the message and fixes its next call:
      </P>
      <CodeBlock>{`} catch (err) {
  // Tool-level errors go back as tool results so the AI can react.
  const message = err instanceof McpToolError
    ? err.message
    : "internal error";
  return rpcResult(id, {
    content: [{ type: "text", text: message }],
    isError: true,
  });
}`}</CodeBlock>
      <P>
        We reserve real JSON-RPC errors for real protocol problems: unknown
        methods, unparseable bodies. That single distinction did more for
        agent success rates than any prompt tweak.
      </P>

      <H2>The schema-reference trick</H2>
      <P>
        Our automations have a wide surface: nine trigger types, twenty-odd
        step types, condition rules, send windows, re-entry policies. Stuffing
        all of that into every tool description bloats{" "}
        <InlineCode>tools/list</InlineCode> for every conversation, whether or
        not the user ever builds anything. Instead we ship a{" "}
        <InlineCode>get_schema_reference</InlineCode> tool that returns the
        full automation contract as one document, and the server{" "}
        <InlineCode>instructions</InlineCode> field tells the model to call it
        once before creating or updating anything.
      </P>
      <Callout>
        Docs-as-a-tool inverts the context problem: the model pulls the
        reference exactly when it needs it, the reference lives in one file
        kept in lockstep with the real TypeScript types, and updating it does
        not touch thirteen tool descriptions.
      </Callout>
      <P>
        The same instructions encode the safety contract: everything an AI
        creates lands as a <strong>draft</strong>, and a separate{" "}
        <InlineCode>set_automation_status</InlineCode> tool flips things live
        only when the user asks. Agents build; humans approve. That split is
        load-bearing — it is the reason we are comfortable letting an agent
        write automations that could email real people.
      </P>

      <H2>Getting listed</H2>
      <P>
        Publishing to the official MCP Registry is refreshingly mechanical:
        a <InlineCode>server.json</InlineCode> describing the remote endpoint,
        domain verification via a well-known file signed with an Ed25519 key,
        and <InlineCode>mcp-publisher publish</InlineCode>. Two practical
        notes: the registry caps descriptions at 100 characters, and the
        ecosystem directories (PulseMCP, Glama, mcp.so) auto-ingest the
        registry — list once, appear everywhere.
      </P>

      <H2>What we would tell you to copy</H2>
      <UL>
        <li>
          Stateless Streamable HTTP is enough for a tools-only server, and it
          turns MCP hosting into &ldquo;add one API route&rdquo;.
        </li>
        <li>
          Scope tenancy at authentication; hand tools an already-scoped
          context.
        </li>
        <li>
          Domain failures are <InlineCode>isError</InlineCode> tool results
          the model can read, never protocol errors.
        </li>
        <li>
          Big schemas belong in a docs tool the model calls on demand, not in
          every tool description.
        </li>
        <li>
          Make &ldquo;draft by default, human flips live&rdquo; part of the server
          contract, not an afterthought in your UI.
        </li>
      </UL>
      <P>
        The server this post describes is live —{" "}
        <A href="/ai">the /ai page</A> has the connection steps and the full
        tool list, and the companion post explains the other half of the
        story:{" "}
        <A href="/blog/two-way-sync-diagram-is-the-automation">
          why the funnel diagram the AI edits is the automation itself
        </A>
        .
      </P>
    </BlogPostLayout>
  );
}
